import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { OPS_KEY, type ChecklistItem } from '../api';

/** ניהול תבניות הצ'קליסט (PRD 5.9.1). */

export type AdminTemplate = {
  id: string;
  name: string;
  is_active: boolean;
  recurrence: 'daily' | 'weekly' | 'monthly' | 'once';
  weekdays: number[] | null;
  day_of_month: number | null;
  on_date: string | null;
  due_time: string | null;
  /** ריק = כל הסניפים. */
  checklist_template_branches: { branch_id: string; branches: { name: string } | null }[];
};

const ADMIN_KEY = ['admin', 'ops'] as const;

export function useTemplates() {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'templates'],
    queryFn: async (): Promise<AdminTemplate[]> => {
      const { data, error } = await supabase
        .from('checklist_templates')
        // דרך טבלת השיוך ולא דרך branch_id: מאז שנוספה
        // checklist_template_branches יש שני מסלולים ל branches,
        // ו PostgREST מחזיר 300 כשלא אומרים לו באיזה מהם מדובר
        .select(
          `id, name, is_active, recurrence, weekdays, day_of_month, on_date, due_time,
           checklist_template_branches ( branch_id, branches ( name ) )`,
        )
        .order('due_time', { nullsFirst: false })
        .order('sort_order')
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as AdminTemplate[];
    },
  });
}

/**
 * המשימות של תבנית, לקריאה חד פעמית.
 *
 * לא הוק: זה נקרא ברגע שפותחים את החלון, ולא בכל רינדור.
 */
export async function fetchTemplateItems(templateId: string) {
  const { data, error } = await supabase
    .from('checklist_template_items')
    .select('id, text, requires_photo')
    .eq('template_id', templateId)
    .order('sort_order');
  if (error) throw error;
  return data ?? [];
}

export function useTemplateItems(templateId: string) {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'items', templateId],
    enabled: Boolean(templateId),
    queryFn: async (): Promise<ChecklistItem[]> => {
      const { data, error } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as ChecklistItem[];
    },
  });
}

export function useTemplateMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    void queryClient.invalidateQueries({ queryKey: OPS_KEY });
  };

  const createTemplate = useMutation({
    mutationFn: async (input: { name: string; branch_id: string | null }) => {
      const { error } = await supabase.from('checklist_templates').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * שמירת תבנית עם הסניפים והחזרתיות, בקריאה אחת.
   *
   * דרך RPC ולא כמה updates: תבנית שנשמרה בלי הסניפים שלה חלה
   * לרגע על כל הרשת, ובזמן הזה היא כבר מופיעה לעובדים.
   */
  const saveTemplate = useMutation({
    mutationFn: async (input: {
      id: string | null;
      name: string;
      branchIds: string[];
      recurrence: string;
      weekdays: number[];
      dayOfMonth: number | null;
      onDate: string | null;
      dueTime: string | null;
    }) => {
      const { data, error } = await supabase.rpc('save_checklist_template', {
        p_id: input.id ?? undefined,
        p_name: input.name,
        p_branches: input.branchIds,
        p_recurrence: input.recurrence,
        p_weekdays: input.recurrence === 'weekly' ? input.weekdays : undefined,
        p_day_of_month:
          input.recurrence === 'monthly' ? (input.dayOfMonth ?? undefined) : undefined,
        p_on_date: input.recurrence === 'once' ? (input.onDate ?? undefined) : undefined,
        p_due_time: input.dueTime ?? undefined,
      });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: refresh,
  });

  /**
   * המשימות של התבנית, כהפרש ולא ככתיבה מחדש.
   *
   * מחיקה וכתיבה מחדש היתה מנתקת את ההיסטוריה: checklist_run_items
   * מצביע על item_id, וכל סימון שנעשה בעבר היה מאבד את המשימה
   * שאליה הוא שייך. לכן קיים מתעדכן, חדש נוסף, ורק מה שהוסר
   * באמת נמחק.
   */
  const saveItems = useMutation({
    mutationFn: async (input: {
      templateId: string;
      items: { id?: string; text: string; requires_photo: boolean }[];
    }) => {
      const { data: existing, error: readError } = await supabase
        .from('checklist_template_items')
        .select('id')
        .eq('template_id', input.templateId);
      if (readError) throw readError;

      const keep = new Set(input.items.map((item) => item.id).filter(Boolean));
      const gone = (existing ?? []).map((row) => row.id).filter((id) => !keep.has(id));

      if (gone.length > 0) {
        const { error } = await supabase
          .from('checklist_template_items')
          .delete()
          .in('id', gone);
        if (error) throw error;
      }

      // sort_order נקבע לפי המקום ברשימה, ולכן גרירה בחלון נשמרת
      for (const [index, item] of input.items.entries()) {
        const values = {
          text: item.text.trim(),
          requires_photo: item.requires_photo,
          sort_order: index,
        };

        const { error } = item.id
          ? await supabase.from('checklist_template_items').update(values).eq('id', item.id)
          : await supabase
              .from('checklist_template_items')
              .insert({ ...values, template_id: input.templateId });
        if (error) throw error;
      }
    },
    onSuccess: refresh,
  });

  const removeTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('checklist_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const addItem = useMutation({
    mutationFn: async (input: {
      template_id: string;
      text: string;
      requires_photo: boolean;
      sort_order: number;
    }) => {
      const { error } = await supabase.from('checklist_template_items').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('checklist_template_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** שינוי שם התבנית או הסניף שלה, בלי למחוק ולבנות מחדש. */
  const updateTemplate = useMutation({
    mutationFn: async (input: { id: string; name: string; branch_id: string | null }) => {
      const { error } = await supabase
        .from('checklist_templates')
        .update({ name: input.name, branch_id: input.branch_id })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** שינוי נוסח הפריט או דרישת הצילום. */
  const updateItem = useMutation({
    mutationFn: async (input: { id: string; text: string; requires_photo: boolean }) => {
      const { error } = await supabase
        .from('checklist_template_items')
        .update({ text: input.text, requires_photo: input.requires_photo })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * הסדר החדש אחרי גרירה, בפקודה אחת. גרירה מזיזה פריט על פני כמה
   * מקומות וכל מי שביניהם זז, ולכן החלפת זוג כבר לא מספיקה.
   */
  const reorderItems = useMutation({
    mutationFn: async (input: { templateId: string; ids: string[] }) => {
      const { error } = await supabase.rpc('reorder_checklist_items', {
        p_template: input.templateId,
        p_ids: input.ids,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return {
    saveTemplate,
    saveItems,
    createTemplate,
    updateTemplate,
    removeTemplate,
    addItem,
    updateItem,
    removeItem,
    reorderItems,
  };
}
